import type { ReactNode } from 'react';
import React, { useMemo, useRef } from 'react';
import type { MantineSpacing, StyleProp } from '@mantine/core';
import { Stack, Breadcrumbs, Title, Container, Space } from '@mantine/core';
import { Link } from 'react-router-dom';
import keys from 'lodash-es/keys';
import useRememberScrollTop from '@dr-pam/common-components/Hooks/useRememberScollTop';
import objectHash from 'object-hash';
import styled from '@emotion/styled';

export type ContentProps = {
	className?: string;
	contentClassName?: string;
	breadcrumbs?: Record<string, string>;
	title?: ReactNode;
	preContent?: ReactNode;
	children?: ReactNode;
	padding?: StyleProp<MantineSpacing>;
	fillHeight?: boolean;
};

const MAX_BREADCRUMB_CHARS = 120;

// Crumbs are `white-space: nowrap`, so a long one can't break and the surrounding ScrollArea,
// which sizes to min-content, drags the whole page wider than the window. CSS alone won't do it.
function shorten(text: string): string {
	return text.length > MAX_BREADCRUMB_CHARS ? `${text.slice(0, MAX_BREADCRUMB_CHARS).trimEnd()}…` : text;
}

export default function Content(props: ContentProps) {
	const { className, breadcrumbs, title, preContent, children, padding, contentClassName, fillHeight } = props;

	const containerEl = useRef<HTMLDivElement>(null);

	const hash = useMemo(() => objectHash([title, className, contentClassName]), [className, contentClassName, title]);
	useRememberScrollTop(containerEl, hash);

	return (
		<StyledStack
			className={`Content container ${className ?? ''} ${fillHeight ? 'fill-height' : ''} ${
				preContent ? 'pre-content' : ''
			}`}
		>
			{breadcrumbs && (
				<Breadcrumbs
					styles={{
						root: { minWidth: 0, maxWidth: '100%' },
						breadcrumb: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
					}}
				>
					{keys(breadcrumbs).map((href) => (
						<Link key={href} to={href} title={breadcrumbs[href]}>
							{shorten(breadcrumbs[href])}
						</Link>
					))}
				</Breadcrumbs>
			)}
			{title && <Title order={1}>{title}</Title>}
			{preContent}
			<Container
				ref={containerEl}
				className={`ContentContainer content ${contentClassName ?? ''}`}
				maw="100%"
				p={padding ?? 'lg'}
			>
				{children}
			</Container>
			<Space />
		</StyledStack>
	);
}

const StyledStack = styled(Stack)`
	.container {
		display: grid;
		grid-template-rows: auto auto auto 1fr;
		grid-template-columns: 1fr;
		overflow: hidden;
		width: 100%;
		height: 100%;

		&.pre-content {
			grid-template-rows: auto auto auto auto 1fr;
		}
		&.fill-height {
			grid-template-rows: auto auto 1fr 0;
			&.pre-content {
				grid-template-rows: auto auto auto 1fr 0;
			}
		}
	}
	.content {
		background: var(--mantine-color-white);
		width: 100%;
		height: 100%;
		margin: 0;
		overflow-y: auto;
	}
`;
